import React from "react";
import "./alertDocumentation.css";
import { alertDocumentationModel } from "./alertDocumentationData";

const SOURCE_FILTERS = [
  { value: "all", label: "All documented events" },
  { value: "device", label: "Configurable on device" },
  { value: "cloud", label: "Cloud-generated" },
];

function matchesSource(entry, sourceFilter) {
  if (sourceFilter === "device") {
    return entry.isConfigurable;
  }

  if (sourceFilter === "cloud") {
    return !entry.isConfigurable;
  }

  return true;
}

function matchesSearch(section, entry, query) {
  if (!query) {
    return true;
  }

  const noteText = (entry.notes || [])
    .map((note) => `${note.title || ""} ${note.description || ""}`)
    .join(" ");
  const parameterText = (entry.parameters || [])
    .map((parameter) =>
      [parameter.label, parameter.unit, parameter.input, parameter.placeholder]
        .filter(Boolean)
        .join(" "),
    )
    .join(" ");

  const haystack = [
    section.title,
    section.summary,
    entry.name,
    entry.code,
    entry.summary,
    entry.triggeredDescription,
    entry.notTriggeredDescription,
    parameterText,
    (entry.applicability || []).join(" "),
    noteText,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(query);
}

export default function AlertDocumentation() {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [sourceFilter, setSourceFilter] = React.useState("all");

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visibleSections = alertDocumentationModel.sections
    .map((section) => ({
      ...section,
      entries: (section.entries || []).filter(
        (entry) =>
          matchesSource(entry, sourceFilter) &&
          matchesSearch(section, entry, normalizedQuery),
      ),
    }))
    .filter((section) => section.entries.length > 0);

  const visibleEventCount = visibleSections.reduce(
    (sum, section) => sum + section.entries.length,
    0,
  );

  return (
    <div className="alert-docs">
      <section className="alert-docs__hero">
        <div className="alert-docs__hero-grid">
          <div className="alert-docs__hero-copy">
            <h2 className="alert-docs__hero-title">Find the right trigger faster</h2>
            <p className="alert-docs__hero-description">
              This page documents the alert triggers available in Synaccess Cloud,
              shows what each trigger monitors, and explains which events are
              configured on the device versus generated by cloud monitoring.
            </p>
          </div>
          <div className="alert-docs__stats">
            <div className="alert-docs__stat">
              <div className="alert-docs__stat-value">
                {alertDocumentationModel.stats.configurableCount}
              </div>
              <div className="alert-docs__stat-label">Configurable triggers</div>
              <div className="alert-docs__stat-note">
                Built directly from the shared alert reference used by the UI.
              </div>
            </div>
            <div className="alert-docs__stat">
              <div className="alert-docs__stat-value">
                {alertDocumentationModel.stats.cloudCount}
              </div>
              <div className="alert-docs__stat-label">Cloud-only events</div>
              <div className="alert-docs__stat-note">
                Documented here for clarity, but not selectable in the alert editor.
              </div>
            </div>
            <div className="alert-docs__stat">
              <div className="alert-docs__stat-value">
                {alertDocumentationModel.stats.sectionCount}
              </div>
              <div className="alert-docs__stat-label">Reference sections</div>
              <div className="alert-docs__stat-note">
                Grouped by the same trigger categories used throughout the product.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="alert-docs__steps">
        <div className="alert-docs__steps-head">
          <div>
            <h3 className="alert-docs__section-title">How alerts work</h3>
            <p className="alert-docs__section-copy">
              Every alert follows the same flow. Pick a trigger, define the
              condition, then attach the actions that should run when that
              condition becomes active.
            </p>
          </div>
        </div>
        <div className="alert-docs__steps-grid">
          <article className="alert-docs__step">
            <span className="alert-docs__step-icon">
              <i aria-hidden="true" className="material-icons">
                sensors
              </i>
            </span>
            <h4 className="alert-docs__step-title">1. Choose a trigger</h4>
            <p className="alert-docs__steps-copy">
              Start with the event that best matches what you want to monitor:
              power, network, sensors, schedules, or ATS state changes.
            </p>
          </article>
          <article className="alert-docs__step">
            <span className="alert-docs__step-icon">
              <i aria-hidden="true" className="material-icons">
                tune
              </i>
            </span>
            <h4 className="alert-docs__step-title">2. Set the condition</h4>
            <p className="alert-docs__steps-copy">
              Parameters define the target, threshold, timing window, or state
              transition that should move the alert into an active state.
            </p>
          </article>
          <article className="alert-docs__step">
            <span className="alert-docs__step-icon">
              <i aria-hidden="true" className="material-icons">
                bolt
              </i>
            </span>
            <h4 className="alert-docs__step-title">3. Attach actions</h4>
            <p className="alert-docs__steps-copy">
              Once the trigger is active, the device can send notifications,
              switch outlets, or run the other actions you attach to the event.
            </p>
          </article>
        </div>
      </section>

      <section className="alert-docs__controls">
        <div className="alert-docs__controls-head">
          <div>
            <h3 className="alert-docs__section-title">Browse the catalog</h3>
            <p className="alert-docs__section-copy">
              Search by event name, code, parameter label, or usage note. Use the
              filter to focus on device-configurable triggers or cloud-generated
              connection events.
            </p>
          </div>
          <div className="alert-docs__results">
            Showing {visibleEventCount} of {alertDocumentationModel.stats.totalEvents} documented events
          </div>
        </div>

        <div className="alert-docs__search-row">
          <div>
            <label className="alert-docs__search-label" htmlFor="alert-docs-search">
              Search the reference
            </label>
            <div className="alert-docs__search-shell">
              <i aria-hidden="true" className="material-icons">
                search
              </i>
              <input
                id="alert-docs-search"
                className="alert-docs__search-input"
                type="search"
                aria-label="Search alert triggers"
                placeholder="Search triggers, codes, or parameters"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              {searchQuery ? (
                <button
                  type="button"
                  className="alert-docs__search-clear"
                  onClick={() => setSearchQuery("")}
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>

          <div>
            <div className="alert-docs__search-label">Filter by source</div>
            <div className="alert-docs__filter-group">
              {SOURCE_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  className={`alert-docs__filter-button${
                    sourceFilter === filter.value
                      ? " alert-docs__filter-button--active"
                      : ""
                  }`}
                  onClick={() => setSourceFilter(filter.value)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {visibleSections.length > 0 ? (
          <nav className="alert-docs__jump-grid" aria-label="Jump to alert category">
            {visibleSections.map((section) => (
              <a
                key={section.id}
                className="alert-docs__jump-link"
                href={`#${section.id}`}
              >
                <div className="alert-docs__jump-meta">
                  <i aria-hidden="true" className="material-icons">
                    {section.icon}
                  </i>
                  <span className="alert-docs__jump-count">
                    {section.entries.length}
                  </span>
                </div>
                <div className="alert-docs__jump-title">{section.title}</div>
                <div className="alert-docs__jump-summary">{section.summary}</div>
              </a>
            ))}
          </nav>
        ) : null}
      </section>

      {visibleSections.length === 0 ? (
        <section className="alert-docs__empty">
          <h3 className="alert-docs__empty-title">No matching alert triggers</h3>
          <p className="alert-docs__empty-copy">
            Try a broader search term or switch back to all documented events.
          </p>
          <button
            type="button"
            className="alert-docs__filter-button alert-docs__empty-button"
            onClick={() => {
              setSearchQuery("");
              setSourceFilter("all");
            }}
          >
            Reset filters
          </button>
        </section>
      ) : null}

      {visibleSections.map((section) => (
        <section key={section.id} className="alert-docs__section" id={section.id}>
          <div className="alert-docs__section-head">
            <div className="alert-docs__section-title-wrap">
              <span className="alert-docs__section-icon">
                <i aria-hidden="true" className="material-icons">
                  {section.icon}
                </i>
              </span>
              <div>
                <h3 className="alert-docs__section-title">{section.title}</h3>
                <p className="alert-docs__section-copy">{section.summary}</p>
              </div>
            </div>
            <div className="alert-docs__section-count">
              {section.entries.length} documented trigger
              {section.entries.length === 1 ? "" : "s"}
            </div>
          </div>

          <div className="alert-docs__card-grid">
            {section.entries.map((entry) => (
              <article className="alert-docs__card" key={entry.id}>
                <div className="alert-docs__card-head">
                  <div>
                    <span className="alert-docs__code">Code {entry.code}</span>
                    <h4 className="alert-docs__title">{entry.name}</h4>
                  </div>
                  <div className="alert-docs__badges">
                    <span className="alert-docs__source-note">
                      {entry.sourceLabel}
                    </span>
                  </div>
                </div>

                <p className="alert-docs__summary">{entry.summary}</p>

                {entry.applicability.length > 0 ? (
                  <ul className="alert-docs__tag-list">
                    {entry.applicability.map((item) => (
                      <li key={`${entry.id}-${item}`} className="alert-docs__tag">
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : null}

                <div className="alert-docs__detail-grid">
                  <section className="alert-docs__panel">
                    <h5 className="alert-docs__panel-title">Parameters</h5>
                    <ul className="alert-docs__param-list">
                      {entry.parameters.length === 0 ? (
                        <li className="alert-docs__param-item">
                          <span className="alert-docs__param-label">
                            No parameters required
                          </span>
                          <span className="alert-docs__param-meta">
                            This event is ready to use without extra inputs.
                          </span>
                        </li>
                      ) : (
                        entry.parameters.map((parameter) => (
                          <li className="alert-docs__param-item" key={parameter.id}>
                            <span className="alert-docs__param-label">
                              {parameter.label}
                            </span>
                            <span className="alert-docs__param-meta">
                              {[parameter.unit, parameter.input]
                                .filter(Boolean)
                                .join(" • ") || "Input required"}
                            </span>
                          </li>
                        ))
                      )}
                    </ul>
                  </section>

                  <section className="alert-docs__panel">
                    <h5 className="alert-docs__panel-title">State descriptions</h5>
                    <div className="alert-docs__status">
                      <div>
                        <span className="alert-docs__status-label alert-docs__status-label--alert">
                          Alert when
                        </span>
                        <p className="alert-docs__status-copy">
                          {entry.triggeredDescription}
                        </p>
                      </div>
                      <div>
                        <span className="alert-docs__status-label alert-docs__status-label--normal">
                          Normal when
                        </span>
                        <p className="alert-docs__status-copy">
                          {entry.notTriggeredDescription}
                        </p>
                      </div>
                    </div>
                  </section>
                </div>

                {entry.notes.length > 0 ? (
                  <section className="alert-docs__panel">
                    <h5 className="alert-docs__panel-title">Usage notes</h5>
                    <ul className="alert-docs__note-list">
                      {entry.notes.map((note, index) => (
                        <li className="alert-docs__note-item" key={`${entry.id}-note-${index}`}>
                          {note.title ? (
                            <span className="alert-docs__note-title">{note.title}</span>
                          ) : null}
                          <p className="alert-docs__note-copy">{note.description}</p>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
